// Estado de conversaciones en tiempo real (mensajes entrantes por SignalR).
// Portado de frontendcongress. Alimenta los badges de no leídos del chat y el
// aviso a expositores cuando alguien entra a su stand.

import Storage from '../../components/Storage';

// --- Registro de visitas al stand, persistido en localStorage ---
// Clave por evento + expositor para no mezclar registros en el mismo navegador.
function visitorsKey() {
  return `standVisitors_${Storage.getIdEvent()}_${Storage.getIdPerson()}`;
}
function loadStandVisitors() {
  try {
    const raw = window.localStorage.getItem(visitorsKey());
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}
function saveStandVisitors(list) {
  try {
    window.localStorage.setItem(visitorsKey(), JSON.stringify(list));
  } catch (e) { /* localStorage lleno o no disponible: no es fatal */ }
}

const initialState = {
  selectedParticipant: 0,
  participantsOnStand: [],
  // { [fromPersonId]: { unreadMessagesCount, unreadMessages: [] } }
  allUnreadMessages: {},
  // Para disparar un toast en cada entrada a stand (aunque la persona ya estuviera).
  lastStandEntry: null, // { fromPersonId, seq }
  standSeq: 0,
  // Registro acumulado de visitas: [{ idPerson, firstTs, lastTs, count }]. Persiste en localStorage.
  standVisitors: loadStandVisitors(),
  // GUIDs de mensajes propios que el server eco-broadcasteó (= entregados).
  // Se usan para mostrar ✓ en las burbujas enviadas.
  deliveredGuids: {},
  // Invitaciones para conectar que esperan MI respuesta: [{ idPerson, firstName,
  // lastName, pictureId, company }]. Salen del poll de mymessages (status
  // 'invitation_received'), no del socket: así el badge del tab Mensajes no
  // depende de que el evento en vivo llegue.
  pendingInvitations: [],
  // Conversaciones ya abiertas en la sesión: una invitación pendiente deja de
  // contar en el badge una vez que el usuario la vio (aunque no la acepte).
  invitationsSeen: {},
  // Último evento de invitación (recibida o aceptada) para toast + sonido:
  // { seq, kind: 'received' | 'accepted', idPerson, name, person }. Se avisa
  // una sola vez por persona y tipo, venga por socket o por poll.
  lastInvitation: null,
  invitationSeq: 0,
  invitationsNotified: {},
};

export default function conversationReducer(state = initialState, action) {
  switch (action.type) {
    case 'ENTER_STAND': {
      const seq = state.standSeq + 1;
      const id = action.payload.fromPersonId;
      const now = Date.now();
      const already = state.participantsOnStand.indexOf(id) !== -1;
      // Upsert en el registro de visitas: si ya visitó, actualiza fecha y suma; si no, lo agrega arriba.
      const exists = state.standVisitors.some((v) => v.idPerson === id);
      const standVisitors = exists
        ? state.standVisitors.map((v) => v.idPerson === id ? { ...v, lastTs: now, count: v.count + 1 } : v)
        : [{ idPerson: id, firstTs: now, lastTs: now, count: 1 }, ...state.standVisitors];
      saveStandVisitors(standVisitors);
      return {
        ...state,
        participantsOnStand: already
          ? state.participantsOnStand
          : [...state.participantsOnStand, id],
        lastStandEntry: { fromPersonId: id, seq },
        standSeq: seq,
        standVisitors,
      };
    }
    case 'CLEAR_STAND_VISITORS': {
      saveStandVisitors([]);
      return { ...state, standVisitors: [] };
    }
    case 'REMOVE_STAND_VISITOR': {
      const standVisitors = state.standVisitors.filter((v) => v.idPerson !== action.payload);
      saveStandVisitors(standVisitors);
      return { ...state, standVisitors };
    }
    case 'LEAVE_STAND': {
      return {
        ...state,
        participantsOnStand: state.participantsOnStand.filter((o) => o !== action.payload.fromPersonId),
      };
    }
    case 'SELECT_PARTICIPANT': {
      const fromPersonId = Number(action.payload);
      return {
        ...state,
        selectedParticipant: fromPersonId,
        allUnreadMessages: { ...state.allUnreadMessages, [fromPersonId]: { unreadMessagesCount: 0, unreadMessages: [] } },
      };
    }
    case 'MARK_AS_READ_ALL_MESSAGES_FROM': {
      const fromPersonId = action.payload;
      // Abrir la conversación también "ve" la invitación pendiente de esa persona.
      const invitationsSeen = state.invitationsSeen[fromPersonId]
        ? state.invitationsSeen
        : { ...state.invitationsSeen, [fromPersonId]: true };
      if (typeof state.allUnreadMessages[fromPersonId] === 'undefined') {
        return invitationsSeen === state.invitationsSeen ? state : { ...state, invitationsSeen };
      }
      return {
        ...state,
        invitationsSeen,
        allUnreadMessages: { ...state.allUnreadMessages, [fromPersonId]: { unreadMessagesCount: 0, unreadMessages: [] } },
      };
    }
    case 'MESSAGE_DELIVERED': {
      const guid = action.payload && action.payload.messageGuid;
      if (!guid) return state;
      return { ...state, deliveredGuids: { ...state.deliveredGuids, [guid]: true } };
    }
    case 'RECEIVE_MESSAGE_FROM_SERVER': {
      const msg = action.payload;
      const fromPersonId = msg.fromPersonId;
      const obj = state.allUnreadMessages[fromPersonId];
      // El mismo mensaje puede entrar por socket y por poll: se cuenta una vez.
      if (msg.messageId && obj && obj.unreadMessages.some((m) => m.messageId === msg.messageId)) return state;
      const count = obj ? obj.unreadMessagesCount + 1 : 1;
      const list = obj ? [...obj.unreadMessages, msg] : [msg];
      return {
        ...state,
        allUnreadMessages: { ...state.allUnreadMessages, [fromPersonId]: { unreadMessagesCount: count, unreadMessages: list } },
      };
    }
    case 'SET_PENDING_INVITATIONS': {
      return { ...state, pendingInvitations: Array.isArray(action.payload) ? action.payload : [] };
    }
    case 'INVITATION_EVENT': {
      const { kind, idPerson } = action.payload || {};
      const key = `${kind}:${idPerson}`;
      if (!kind || idPerson == null || state.invitationsNotified[key]) return state;
      const seq = state.invitationSeq + 1;
      return {
        ...state,
        invitationSeq: seq,
        lastInvitation: { ...action.payload, seq },
        invitationsNotified: { ...state.invitationsNotified, [key]: true },
      };
    }
    default:
      return state;
  }
}
